import { useRef, useState } from 'react';
import PageShell from '@components/pages/PageShell';
import {
    ResourceHero,
    ArticleCard,
    Pagination,
    SectionLabel,
    TopAndRecent,
    EmptyList,
} from '@components/pages/ResourceSections';
import Newsletter from '@components/landing/Newsletter';
import { usePagedList } from '@/lib/usePagedList';
import { STORIES, TOP_STORY, RECENT_STORIES } from '@/data/resources';
import Seo from '@components/Seo';
import { CardGridSkeleton, TopAndRecentSkeleton, useSiteLoading } from '@components/Skeleton';

const BASE = '/resources/stories';

/** Three rows of the four-column grid. */
const PER_PAGE = 12;

export default function Stories() {
    const [query, setQuery] = useState('');
    const grid = useRef<HTMLElement>(null);
    const { page, setPage, pages, matches, pageItems } = usePagedList(
        STORIES,
        PER_PAGE,
        query,
        (s) => `${s.title} ${s.excerpt ?? ''} ${s.searchText ?? s.body?.join(' ') ?? ''}`,
    );
    const searching = query.trim().length > 0;
    const loading = useSiteLoading();

    const goTo = (next: number) => {
        setPage(next);
        grid.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    return (
        <PageShell>
            <Seo
                title="Success Stories"
                description="Behind every case resolved is a person who finally found a way forward. These are their accounts, written up from the files."
            />
            <ResourceHero
                titleLead="Our"
                titleBold="Stories"
                intro="Behind every case resolved is a person who finally found a way forward. These are their accounts, written up from the files."
                query={query}
                onSearch={setQuery}
                searchPlaceholder="Search stories"
            />

            {!searching &&
                (TOP_STORY.title ? (
                    <TopAndRecent
                        top={TOP_STORY}
                        recent={RECENT_STORIES}
                        topLabel="Top Story"
                        recentLabel="Recent Stories"
                        basePath={BASE}
                    />
                ) : loading ? (
                    <TopAndRecentSkeleton />
                ) : null)}

            <section ref={grid} className="scroll-mt-24 bg-white pb-14 pt-10 lg:pb-20" aria-live="polite">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                        <SectionLabel>{searching ? 'Search results' : 'All Stories'}</SectionLabel>
                        <p className="text-[13px] text-[#5e5e5e]">
                            {searching
                                ? `${matches.length} ${matches.length === 1 ? 'story' : 'stories'} for “${query.trim()}”`
                                : `${STORIES.length} stories`}
                        </p>
                    </div>

                    {pageItems.length > 0 ? (
                        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                            {pageItems.map((s) => (
                                <ArticleCard key={s.slug} item={s} basePath={BASE} />
                            ))}
                        </div>
                    ) : loading ? (
                        <CardGridSkeleton count={PER_PAGE} />
                    ) : (
                        <EmptyList
                            query={query}
                            whenSearching={`No stories match “${query.trim()}”. Try a name, a place or a word from the headline.`}
                        />
                    )}

                    <Pagination page={page} pages={pages} onChange={goTo} />
                </div>
            </section>

            <Newsletter />
        </PageShell>
    );
}
